import {StyleSheet} from "aphrodite";
import {
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useReducer,
    useRef,
    useState,
} from "react";

import SimpleMarkdown from "@khanacademy/simple-markdown";
import Banner from "@khanacademy/wonder-blocks-banner";
import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {BodyMonospace} from "@khanacademy/wonder-blocks-typography";
import {Markdown} from "@khan/component-markdown";
import {SaveIndicator, type SavedStatus} from "@khan/component-save-indicator";
import {usePrefersReducedMotion} from "@khan/data-prefers-reduced-motion";
import {GuideAccessGate} from "@khan/guide-core";
import {setPromptVariable} from "@khan/guide-core-helpers";
import {doNotTranslateYet} from "@khan/i18n";
import Log, {Errors} from "@khan/logging";
import {mediaQueries} from "@khan/shared-styles";

import type {
    ComputingChallengeStepTest,
    ComputingCodeFormat,
    ComputingCodeMultiFile,
} from "#content-library/types/types.ts";
import FullPageContentContext from "#content-library/ui/full-page-content-context.ts";
import useProfile from "#data-access/use-profile.ts";

import {
    computingCodeMultiFileToKhanmigoString,
    jsonStringToComputingCodeMultiFile,
} from "./code-formatting.ts";
import InstructionsTitleCell from "./instructions-title-cell.tsx";
import MobileTabList, {type MobileTabName} from "./mobile-tab-list.tsx";
import PythonOutputFrame from "./python-output-frame.tsx";
import RunButton from "./run-button.tsx";
import {
    sendRunPythonCode,
    sendStopPythonCode,
    subscribeToEventsFromSandbox,
    type SandboxMessage,
} from "./sandbox-api.ts";
import {useSelectMenuState} from "./select-menu.tsx";
import TabbedCodeEditor from "./tabbed-code-editor.tsx";

import sidebarSimpleFillIcon from "@phosphor-icons/core/fill/sidebar-simple-fill.svg";
import caretLeftIcon from "@phosphor-icons/core/regular/caret-left.svg";
import caretRightIcon from "@phosphor-icons/core/regular/caret-right.svg";
import cornersInIcon from "@phosphor-icons/core/regular/corners-in.svg";
import cornersOutIcon from "@phosphor-icons/core/regular/corners-out.svg";
import sidebarSimpleIcon from "@phosphor-icons/core/regular/sidebar-simple.svg";

const expandedPanelIcon = sidebarSimpleFillIcon;
const collapsedPanelIcon = sidebarSimpleIcon;

const fullscreenExpandIcon = cornersOutIcon;
const fullscreenShrinkIcon = cornersInIcon;

type Props = {
    // The structure of the code.
    codeFormat: ComputingCodeFormat;

    // When true, will hide the UI elements to enter and exit full page mode.
    hideFullPageContentUI?: boolean;

    // The code that the editor will start with.
    initialCodeJSON: string;

    // A description of the program in the current locale.
    instructions?: string;

    // Metadata fields to apply to error logs.
    loggingMetadata: Record<string, string>;

    // A callback that gets called whenever the code in the editor changes.
    onCodeChange?: (
        code: ComputingCodeMultiFile,
        codeFormat: ComputingCodeFormat,
    ) => void;

    // A callback that gets called when the user clicks the "Run" button to run
    // the code.
    onRun?: () => void;

    // A callback that gets called when the user's code fails one or more tests.
    onTestsFailed?: (
        status: "FAILED" | "ERROR",
        failedTestName: string,
    ) => void;

    // A callback that gets called when the user's code passes all the tests.
    onTestsPassed?: () => void;

    // A function that renders a Footer component that will be shown at the
    // bottom of the page.
    renderFooter: (footerProps: {
        runButton: React.ReactNode;
        code: ComputingCodeMultiFile;
    }) => React.ReactNode;

    // Props for a button to manually save the code, if applicable.
    saveButton?: {
        disabled: boolean;
        spinner: boolean;
        onClick: () => void;
    };

    // The current saved status of the coding environment.
    savedStatus: SavedStatus;

    // A label describing what step the user is currently on, which is shown
    // in the instructions panel. This is only used for Challenges.
    stepLabel?: string;

    // The tests that will be run on the code; this only applies to Challenges.
    tests?: Array<ComputingChallengeStepTest> | null;

    // An optional title for the page, which will appear above the description.
    title?: string;

    // True if the user is allowed to create, delete, and rename files.
    // Defaults to false.
    canManageFiles?: boolean;

    // True if the program is being displayed as an embedded program.
    useEmbeddedStyles?: boolean;
};

export type WorkerStatus = "initializing" | "ready" | "running";

type EditorState = {
    code: ComputingCodeMultiFile;
    workerStatus: WorkerStatus;
};

type Action =
    | {type: "WORKER_READY"}
    | {type: "RUN_CODE"; code: ComputingCodeMultiFile}
    | {type: "UPDATE_CODE"; code: ComputingCodeMultiFile};

const reducer = (state: EditorState, action: Action): EditorState => {
    switch (action.type) {
        case "WORKER_READY":
            return {
                ...state,
                workerStatus: "ready",
            };
        case "RUN_CODE":
            return {
                ...state,
                code: action.code,
                workerStatus: "running",
            };
        case "UPDATE_CODE":
            return {...state, code: action.code};
    }
};

type DesktopPanelStates = {
    areInstructionsExpanded: boolean;
    isOutputExpanded: boolean;
};

function desktopPanelStatesReducer(
    state: DesktopPanelStates,
    action: "TOGGLE_INSTRUCTIONS" | "TOGGLE_OUTPUT",
): DesktopPanelStates {
    switch (action) {
        case "TOGGLE_INSTRUCTIONS":
            return {
                ...state,
                areInstructionsExpanded: !state.areInstructionsExpanded,
            };
        case "TOGGLE_OUTPUT":
            return {
                ...state,
                isOutputExpanded: !state.isOutputExpanded,
            };
    }
}

const embeddedBreakpoints =
    new URLSearchParams(location.search).get("embeddedBreakpoints") === "true";

export default function PythonCodingPage({
    codeFormat,
    hideFullPageContentUI,
    loggingMetadata,
    initialCodeJSON,
    instructions,
    onCodeChange,
    onRun,
    onTestsFailed,
    onTestsPassed,
    renderFooter,
    saveButton,
    savedStatus,
    stepLabel,
    tests,
    title,
    canManageFiles,
    useEmbeddedStyles,
}: Props): React.ReactElement {
    // To avoid breaking memoization when the loggingMetadata object isn't
    // stable, we convert it to a string, and then parse the fields from the
    // string when we need them.
    const metadataString = Object.keys(loggingMetadata)
        .map((key) => `${key}:${loggingMetadata[key]}`)
        .join(",");
    // Not only is parsing the JSON string slow, the resulting object needs to
    // be stable for the tabbed editor to avoid re-initializing the CodeMirror
    // editor. So, we memoize the value.
    const initialCode = useMemo(() => {
        try {
            return jsonStringToComputingCodeMultiFile(
                initialCodeJSON,
                codeFormat,
            );
        } catch (err) {
            const reconstructedMetadata: Record<string, string> = {};
            metadataString.split(",").forEach((pair) => {
                const [key, value] = pair.split(":");
                reconstructedMetadata[key] = value;
            });
            if (err instanceof TypeError) {
                Log.error(
                    "Unknown codeFormat value in Python program",
                    Errors.InvalidInput,
                    {
                        cause: err,
                        metadata: {...reconstructedMetadata, codeFormat},
                    },
                );
            } else if (err instanceof Error) {
                Log.error(
                    "Error parsing initialCodeJSON in Python program",
                    Errors.InvalidInput,
                    {
                        cause: err,
                        metadata: {
                            ...reconstructedMetadata,
                            codeFormat,
                            errorMessage: err.message,
                        },
                    },
                );
            }

            throw err;
        }
    }, [initialCodeJSON, codeFormat, metadataString]);

    const {isContentFullPage, setIsContentFullPage} = useContext(
        FullPageContentContext,
    );

    const [editorState, dispatch] = useReducer(reducer, {
        code: initialCode,
        workerStatus: "initializing",
    });

    const [selectedFilename, setSelectedFilename] = useState(
        initialCode.files[0].filename,
    );
    const mobileFileSelectMenuTriggerRef = useRef<HTMLButtonElement | null>(
        null,
    );
    const mobileFileSelect = useSelectMenuState(mobileFileSelectMenuTriggerRef);

    const [banner, setBanner] = useState<{
        kind: "critical" | "warning";
        message: string;
    } | null>(null);

    const profileResult = useProfile();
    const profile =
        profileResult.status === "success" ? profileResult.profile : null;

    const updateCodePromptVariable = useCallback(
        (code: ComputingCodeMultiFile) => {
            if (profile?.get("isAIGuideEnabled")) {
                const formattedCode =
                    computingCodeMultiFileToKhanmigoString(code);
                setPromptVariable("code", formattedCode);
            }
        },
        [profile],
    );

    useEffect(() => {
        updateCodePromptVariable(initialCode);
    }, [initialCode, updateCodePromptVariable]);

    const [desktopPanelStates, dispatchDesktopPanelStates] = useReducer(
        desktopPanelStatesReducer,
        {
            areInstructionsExpanded: true,
            isOutputExpanded: true,
        },
    );
    const [mobileSelectedTab, setMobileSelectedTab] = useState<MobileTabName>(
        instructions == null ? "editor" : "instructions",
    );

    const [outputFocused, setOutputFocused] = useState(false);

    const instructionsPanelId = useId();
    const outputPanelId = useId();

    const instructionsContainerId = useId();
    const codeEditorContainerId = useId();
    const outputContainerId = useId();

    const prefersReducedMotion = usePrefersReducedMotion();

    const handleCodeChange = useCallback(
        (code: ComputingCodeMultiFile) => {
            dispatch({type: "UPDATE_CODE", code});
            onCodeChange?.(code, codeFormat);
            updateCodePromptVariable(code);
        },
        [onCodeChange, codeFormat, updateCodePromptVariable],
    );

    const outputRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        const handleMessage = (message: MessageEvent<SandboxMessage>) => {
            switch (message.data.type) {
                case "WORKER_READY":
                    dispatch({type: "WORKER_READY"});
                    break;
                case "TEST_RESULTS":
                    const {status} = message.data;
                    if (status === "PASSED") {
                        onTestsPassed?.();
                    } else {
                        onTestsFailed?.(status, message.data.failedTestName);
                        setBanner({
                            kind: status === "ERROR" ? "critical" : "warning",
                            message: message.data.failureMessage,
                        });
                    }
                    break;
                case "OUTPUT_FOCUSED":
                    setOutputFocused(true);
                    break;
                case "OUTPUT_BLURRED":
                    setOutputFocused(false);
                    break;
                case "CRITICAL_ERROR":
                    setBanner({
                        kind: "critical",
                        message: message.data.message,
                    });
                    break;
                default:
            }
        };

        // Subscribe to events from the iframe.
        const subscription = subscribeToEventsFromSandbox(handleMessage);
        return () => {
            // Clean up the subscription on unmount.
            subscription.remove();
        };
    }, [onTestsFailed, onTestsPassed]);

    const runCode = () => {
        dispatch({type: "RUN_CODE", code: editorState.code});
        setBanner(null);

        // Send a message to the iframe telling it to run the code.
        sendRunPythonCode(
            outputRef.current?.contentWindow,
            editorState.code,
            tests,
        );

        onRun?.();
    };

    const stopRunningCode = () => {
        sendStopPythonCode(outputRef.current?.contentWindow);
    };

    const styles =
        useEmbeddedStyles || embeddedBreakpoints
            ? embeddedStyles
            : standaloneStyles;

    return (
        <>
            <View
                style={[
                    styles.platformContainer,
                    isContentFullPage && styles.noBorder,
                ]}
            >
                <View style={[styles.platformHeader, styles.hiddenOnMobile]}>
                    <SaveIndicator
                        labels={{
                            error: doNotTranslateYet`Error saving program`,
                            saved: doNotTranslateYet`Saved`,
                            saving: doNotTranslateYet`Saving...`,
                            ariaError: doNotTranslateYet`There are unsaved code changes`,
                            ariaSaved: doNotTranslateYet`Code changes saved`,
                            ariaSaving: doNotTranslateYet`Saving code changes`,
                        }}
                        size="medium"
                        status={savedStatus}
                    />
                    <View style={styles.headerButtons}>
                        <RunButton
                            onRunCode={runCode}
                            onStopRunningCode={stopRunningCode}
                            size="small"
                            status={editorState.workerStatus}
                            useEmbeddedStyles={
                                useEmbeddedStyles || embeddedBreakpoints
                            }
                        />
                        {saveButton != null && (
                            <Button size="small" {...saveButton}>
                                {doNotTranslateYet`Save`}
                            </Button>
                        )}
                    </View>
                    <View style={styles.actions}>
                        {instructions != null && (
                            <IconButton
                                aria-label={
                                    desktopPanelStates.areInstructionsExpanded
                                        ? doNotTranslateYet`Collapse instructions panel`
                                        : doNotTranslateYet`Expand instructions panel`
                                }
                                icon={
                                    desktopPanelStates.areInstructionsExpanded
                                        ? expandedPanelIcon
                                        : collapsedPanelIcon
                                }
                                size="small"
                                style={styles.toggleIcon}
                                kind="tertiary"
                                actionType="neutral"
                                onClick={() =>
                                    dispatchDesktopPanelStates(
                                        "TOGGLE_INSTRUCTIONS",
                                    )
                                }
                                aria-expanded={
                                    desktopPanelStates.areInstructionsExpanded
                                }
                                aria-controls={instructionsPanelId}
                            />
                        )}
                        <IconButton
                            aria-label={
                                desktopPanelStates.isOutputExpanded
                                    ? doNotTranslateYet`Collapse output panel`
                                    : doNotTranslateYet`Expand output panel`
                            }
                            icon={
                                desktopPanelStates.isOutputExpanded
                                    ? expandedPanelIcon
                                    : collapsedPanelIcon
                            }
                            size="small"
                            style={[styles.toggleIcon, styles.mirroredIcon]}
                            kind="tertiary"
                            actionType="neutral"
                            onClick={() =>
                                dispatchDesktopPanelStates("TOGGLE_OUTPUT")
                            }
                            aria-expanded={desktopPanelStates.isOutputExpanded}
                            aria-controls={outputPanelId}
                        />
                        {!hideFullPageContentUI && (
                            <IconButton
                                aria-label={
                                    isContentFullPage
                                        ? doNotTranslateYet`Exit fullscreen mode`
                                        : doNotTranslateYet`Enter fullscreen mode`
                                }
                                icon={
                                    isContentFullPage
                                        ? fullscreenShrinkIcon
                                        : fullscreenExpandIcon
                                }
                                size="small"
                                style={[styles.toggleIcon]}
                                kind="tertiary"
                                actionType="neutral"
                                onClick={() =>
                                    setIsContentFullPage(!isContentFullPage)
                                }
                            />
                        )}
                    </View>
                </View>
                {savedStatus === "error" && (
                    <View style={styles.errorSavingBanner}>
                        <Banner
                            kind="warning"
                            text={doNotTranslateYet`Error saving program`}
                        />
                    </View>
                )}
                <MobileTabList
                    hasInstructions={instructions != null}
                    selectedTab={mobileSelectedTab}
                    setSelectedTab={setMobileSelectedTab}
                    selectedFilename={selectedFilename}
                    onToggleFileSelectMenu={
                        canManageFiles || editorState.code.files.length > 1
                            ? mobileFileSelect.onToggle
                            : undefined
                    }
                    fileSelectMenuTriggerRef={mobileFileSelectMenuTriggerRef}
                    contentIds={{
                        instructions: instructionsContainerId,
                        editor: codeEditorContainerId,
                        output: outputContainerId,
                    }}
                    useEmbeddedStyles={useEmbeddedStyles || embeddedBreakpoints}
                />
                <View style={styles.panelsContainer}>
                    {instructions != null && (
                        <View
                            style={[
                                styles.panel,
                                styles.sidePanel,
                                !prefersReducedMotion &&
                                    styles.sidePanelAnimated,
                                styles.leftPanel,
                                desktopPanelStates.areInstructionsExpanded
                                    ? styles.sidePanelExpanded
                                    : styles.sidePanelCollapsed,
                                mobileSelectedTab !== "instructions" &&
                                    styles.hiddenOnMobile,
                            ]}
                            id={instructionsContainerId}
                        >
                            <View
                                aria-live="polite"
                                style={[
                                    desktopPanelStates.areInstructionsExpanded &&
                                        !prefersReducedMotion &&
                                        styles.delayedReappear,
                                    !desktopPanelStates.areInstructionsExpanded &&
                                        styles.panelHiddenOnDesktop,
                                    styles.instructionsContainer,
                                ]}
                                id={instructionsPanelId}
                            >
                                <h2 className="sr-only">
                                    {doNotTranslateYet`Instructions`}
                                </h2>
                                {title && (
                                    <InstructionsTitleCell
                                        title={title}
                                        stepLabel={stepLabel}
                                    />
                                )}
                                <View style={styles.instructionsBody}>
                                    <Markdown
                                        source={instructions}
                                        // Overriding the default rendering for inline
                                        // code so that we can use our own styling -
                                        // mostly because the default styles prevents
                                        // inline code from wrapping, and we want it to
                                        // wrap.
                                        overrideRules={{
                                            inlineCode: {
                                                ...SimpleMarkdown.defaultRules
                                                    .inlineCode,
                                                react: (
                                                    node,
                                                    output,
                                                    state,
                                                ) => (
                                                    <BodyMonospace
                                                        key={state.key}
                                                        style={
                                                            styles.inlineCode
                                                        }
                                                        tag="code"
                                                    >
                                                        {node.content}
                                                    </BodyMonospace>
                                                ),
                                            },
                                        }}
                                    />
                                </View>
                            </View>
                            {!desktopPanelStates.areInstructionsExpanded && (
                                <IconButton
                                    aria-label={doNotTranslateYet`Expand instructions panel`}
                                    icon={caretRightIcon}
                                    style={styles.expandButton}
                                    onClick={() =>
                                        dispatchDesktopPanelStates(
                                            "TOGGLE_INSTRUCTIONS",
                                        )
                                    }
                                    aria-expanded={false}
                                    aria-controls={instructionsPanelId}
                                />
                            )}
                        </View>
                    )}

                    <View
                        style={[
                            styles.panel,
                            styles.middlePanel,
                            mobileSelectedTab !== "editor" &&
                                styles.hiddenOnMobile,
                        ]}
                        id={codeEditorContainerId}
                    >
                        <h2 className="sr-only">
                            {doNotTranslateYet`Code editor`}
                        </h2>
                        <TabbedCodeEditor
                            initialCode={initialCode}
                            onCodeChange={handleCodeChange}
                            selectedFilename={selectedFilename}
                            onSelectedFilenameChange={setSelectedFilename}
                            canManageFiles={canManageFiles}
                            mobileFileSelect={mobileFileSelect}
                            useEmbeddedStyles={
                                useEmbeddedStyles || embeddedBreakpoints
                            }
                        />
                    </View>

                    <View
                        style={[
                            styles.panel,
                            styles.sidePanel,
                            !prefersReducedMotion && styles.sidePanelAnimated,
                            styles.rightPanel,
                            desktopPanelStates.isOutputExpanded
                                ? styles.sidePanelExpanded
                                : styles.sidePanelCollapsed,
                            mobileSelectedTab !== "output" &&
                                styles.hiddenOnMobile,
                            outputFocused && styles.sidePanelFocused,
                        ]}
                        id={outputContainerId}
                    >
                        <View
                            style={[
                                styles.outputFrameContainer,
                                desktopPanelStates.isOutputExpanded &&
                                    !prefersReducedMotion &&
                                    styles.delayedReappear,
                                !desktopPanelStates.isOutputExpanded &&
                                    styles.panelHiddenOnDesktop,
                            ]}
                            id={outputPanelId}
                        >
                            <h2 className="sr-only">
                                {doNotTranslateYet`Output`}
                            </h2>
                            <PythonOutputFrame ref={outputRef} />
                        </View>
                        {!desktopPanelStates.isOutputExpanded && (
                            <IconButton
                                aria-label={doNotTranslateYet`Expand output panel`}
                                icon={caretLeftIcon}
                                style={styles.expandButton}
                                onClick={() =>
                                    dispatchDesktopPanelStates("TOGGLE_OUTPUT")
                                }
                                aria-expanded={false}
                                aria-controls={outputPanelId}
                            />
                        )}
                    </View>
                </View>
                <View style={styles.floatingBannerContainer}>
                    {/* TODO (CL-1130): UX fixes/polish for the banner */}
                    {banner && (
                        <Banner
                            kind={banner.kind}
                            text={banner.message}
                            onDismiss={() => setBanner(null)}
                        />
                    )}
                    <GuideAccessGate>
                        {/* if the AI guide chatbot is present, push the banner to
            the left so there's no overlap. */}
                        <Strut size={80} />
                    </GuideAccessGate>
                </View>
            </View>
            {renderFooter({
                runButton: (
                    <RunButton
                        onRunCode={runCode}
                        onStopRunningCode={stopRunningCode}
                        status={editorState.workerStatus}
                        style={styles.hiddenOnDesktop}
                    />
                ),
                code: editorState.code,
            })}
        </>
    );
}

const animationLength = "0.2s";

/* eslint-disable react-native/no-unused-styles */
const getStyles = (useEmbeddedStyles: boolean) =>
    StyleSheet.create({
        platformContainer: {
            display: "flex",
            // This acts as a minimum height so the coding UI doesn't shrink so much
            // that it's unusable.
            flexBasis: 330,
            flexShrink: 0,
            flexGrow: 1,
            borderTop: `1px solid ${color.offBlack16}`,
            // Because the panel buttons are pulled right with a negative
            // margin, they overflow slightly in embedded pages.
            // This is needed to prevent a horizontal scroll bar appearing.
            overflow: useEmbeddedStyles ? "hidden" : "visible",
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                padding:
                    useEmbeddedStyles && !embeddedBreakpoints
                        ? 0
                        : spacing.large_24,
                paddingTop: 0,
                paddingBottom: spacing.small_12,
            },
        },
        noBorder: {
            border: "none",
        },
        platformHeader: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            padding: spacing.small_12,
            paddingLeft: 0,
            paddingRight: 0,
            // Align the buttons to the right side of the rightmost panel.
            // This is needed because the buttons have internal whitespace.
            marginRight: -6,
            alignItems: "center",
            flexShrink: 0,
        },
        headerButtons: {
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            gap: spacing.small_12,
        },
        actions: {
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
        },
        mirroredIcon: {
            transform: "scaleX(-1)",
        },
        toggleIcon: {
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                display: "none",
            },
        },
        panelsContainer: {
            display: "flex",
            flexGrow: 1,
            flexDirection: "row",
            gap: spacing.small_12,
        },
        panel: {
            overflow: "hidden",
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                border: `1px solid ${color.offBlack16}`,
                borderRadius: 10,
                flexShrink: 0,
            },
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                flexGrow: 1,
            },
        },
        sidePanel: {
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                backgroundColor: color.offWhite,
            },
        },
        sidePanelAnimated: {
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                transition: `flex-basis ${animationLength} ease-in-out`,
            },
        },
        sidePanelExpanded: {
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                flex: `1 0 23%`,
                minWidth: 100,
            },
        },
        sidePanelCollapsed: {
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                flexBasis: 20,
            },
        },
        sidePanelFocused: {
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                border: `1px solid ${color.blue}`,
            },
        },
        hiddenOnMobile: {
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                display: "none",
            },
        },
        hiddenOnDesktop: {
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                display: "none",
            },
        },
        panelHiddenOnDesktop: {
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                // we don't use display: none here because we want to set opacity to 0,
                // in order to use transition to delay making the element visible again.
                // if we used display: none, even if we set opacity: 0 in conjunction
                // with it, the transition wouldn't happen and the element would
                // immediately reappear.
                visibility: "hidden",
                maxWidth: 0,
                maxHeight: 0,
                opacity: 0,
            },
        },
        delayedReappear: {
            // applying this style when removing the panelHiddenOnDesktop style will
            // cause the element to reappear only after the animation has completed.
            // this prevents weird text jittering while the width is being animated.
            transition: "opacity 0s",
            transitionDelay: animationLength,
        },
        leftPanel: {
            color: color.offBlack,
        },
        instructionsBody: {
            padding: spacing.medium_16,
            overflowY: "auto",
            height: "100%",
        },
        instructionsContainer: {
            height: "100%",
        },
        middlePanel: {
            padding: 0,
            flex: `1 0 50%`,
            minWidth: 300,
            // Lift the middle panel's stacking context above the other two panels',
            // so that the CodeMirror tooltips don't get overlaid by the other
            // panels.
            zIndex: 1,
        },
        rightPanel: {
            paddingRight: 0,
            paddingBottom: 0,
            backgroundColor: color.offWhite,
        },
        outputFrameContainer: {
            flexGrow: 1,
        },
        expandButton: {
            width: "100%",
            flexGrow: 1,
            margin: 0,
            padding: 0,
            borderRadius: 10,
            boxShadow: "none",
            color: color.offBlack64,
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                display: "none",
            },
        },
        floatingBannerContainer: {
            position: "absolute",
            marginBottom: spacing.medium_16,
            bottom: 0,
            right: spacing.small_12,
            marginLeft: spacing.small_12,
            display: "flex",
            flexDirection: "row",
        },
        inlineCode: {
            fontSize: "12px",
            lineHeight: "22px",
            color: "inherit",
            display: "inline",
            padding: `${spacing.xxxSmall_4}px ${spacing.xxSmall_6}px`,
            margin: 0,
            backgroundColor: "rgba(175,184,193,0.2)",
            borderRadius: 4,
        },
        errorSavingBanner: {
            padding: spacing.xxxSmall_4,
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                display: "none",
            },
        },
    });

const embeddedStyles = getStyles(true);
const standaloneStyles = getStyles(false);
